import { z } from 'zod';
import { ProjectVersion } from './AMProject';
import { ProjectLogEntry, ProjectVersionExecutionLogEntry } from "./ProjectLogEntry";
import { ValueType } from "../ps-models/line-items";
import { WhatIfScenarioConfig } from './CompanyConfigs';

export const WhatIfScenarioInputDef = z.object({
  valueKey: z.string(),
  //Value is (string | number)[][]
  value: z.number().or(z.string()).or(z.boolean()).array().array(),});

export type WhatIfScenarioInput = z.infer<typeof WhatIfScenarioInputDef>;

export const WhatIfScenarioInputValuesDef = z.record(z.string(), WhatIfScenarioInputDef);
export type WhatIfScenarioInputValues = z.infer<typeof WhatIfScenarioInputValuesDef>;

export const WhatIfScenarioTypeEnum = z.enum(["global", "project-version", "new-projects", "clone-project-with-project-version", "debt-funding-scenario", "pfa-scenario", "project-pricing-scenario", "create-project"]);
type WhatIfScenarioType = z.infer<typeof WhatIfScenarioTypeEnum>;


export const CreateWhatIfScenarioRequestDef = z.object({
  type: WhatIfScenarioTypeEnum,
  configurationName: z.string(),
  scenarioName: z.string(),
  projectVersions: z.any().array(),
  globalInputs: WhatIfScenarioInputValuesDef.optional().nullable(),
  projectVersionInputs: z.record(z.string(), WhatIfScenarioInputValuesDef).optional().nullable(),
  parameters: z.record(z.string(), z.number().or(z.string()).or(z.boolean())).optional().nullable(),
});
export type CreateWhatIfScenarioRequest = z.infer<typeof CreateWhatIfScenarioRequestDef>;

export const UpdateWhatIfScenarioRequestDef = z.object({
  scenarioName: z.string(),
  modelId: z.optional(z.string()),
  projectVersions: z.any().array(),
  globalInputs: z.record(z.string(), WhatIfScenarioInputDef).optional().nullable(),
  projectVersionInputs: z.record(z.string(), z.record(z.string(), WhatIfScenarioInputDef)).optional().nullable(),
  parameters: z.record(z.string(), z.number().or(z.string()).or(z.boolean())).optional().nullable(),
});
export type UpdateWhatIfScenarioRequest = z.infer<typeof UpdateWhatIfScenarioRequestDef>;


export type WhatIfScenarioValuesPerProject = Record<string, WhatIfScenarioInputValues>;

export interface WhatIfScenarioRecord {
  companyId: string,
  scenarioId: string,
  modelId?: string,
  scenarioName: string,
  type: WhatIfScenarioType,
  configurationName: string,
  inputData: {
    projectVersions: ProjectVersion[],
    globalInputs?: WhatIfScenarioInputValues
    projectVersionInputs?:WhatIfScenarioValuesPerProject,
    parameters?: Record<string, ValueType>
  }
  status: "new" | "started"  | "completed" | "failed" | "partially-completed"
  log?: ProjectLogEntry[]
}

export type ListWhatIfScenariosResponse = Omit<WhatIfScenarioRecord, "inputData" | "log">[];

export const isFinalStateOfScenario = (status: WhatIfScenarioRecord['status']) => {
  return ["completed", "failed", "partially-completed"].includes(status);
}

export const isFinalStateOfProjectVersion = (status: ProjectVersionExecutionLogEntry['status']) => {
  return ["completed", "failed"].includes(status);
}

export const PFAScenarioConfiguration: WhatIfScenarioConfig & any  = {
  "mappingConfig": {
    "pv-size": {
      "locator": {
        "label": "#B2",
        "type": "cell",
        // "direction": "right",
        "workSheet": "Inputs"
      },
      "valueKey": "pv-size",
      "label": "PV size (kW)"
    },
    "ess-size": {
      "locator": {
        "label": "#B3",
        "type": "cell",
        // "direction": "right",
        "workSheet": "Inputs"
      },
      "valueKey": "ess-size",
      "label": "ESS size (kWh)"
    },
    "number-of-chargers": {
      "locator": {
        "label": "#B4",
        "type": "cell",
        // "direction": "right",
        "workSheet": "Inputs"
      },
      "valueKey": "number-of-chargers",
      "label": "Number of chargers (Units)"
    },
    "total-project-cost": {
      "locator": {
        "label": "#B7",
        "type": "cell",
        // "direction": "right",
        "workSheet": "Inputs"
      },
      "valueKey": "total-project-cost",
      "label": "Total Project Cost"
    },
    "project-annual-revenue": {
      "locator": {
        "label": "#B13",
        "type": "cell",
        // "direction": "right",
        "workSheet": "Inputs"
      },
      "valueKey": "project-annual-revenue",
      "label": "Project Annual Revenue"
    },
    "revenue-escalator": {
      "locator": {
        "label": "#D13",
        "type": "cell",
        // "direction": "right",
        "workSheet": "Inputs"
      },
      "valueKey": "revenue-escalator",
      "label": "Revenue Escalator"
    },
    "project-annual-opex": {
      "locator": {
        "label": "#B14",
        "type": "cell",
        // "direction": "right",
        "workSheet": "Inputs"
      },
      "valueKey": "project-annual-opex",
      "label": "Project Annual Opex"
    },
    "opex-escalator": {
      "locator": {
        "label": "#D14",
        "type": "cell",
        // "direction": "right",
        "workSheet": "Inputs"
      },
      "valueKey": "opex-escalator",
      "label": "Opex Escalator"
    },
    "developer-fee": {
      "locator": {
        "label": "#B6",
        "type": "cell",
        // "direction": "right",
        "workSheet": "Inputs"
      },
      "valueKey": "developer-fee",
      "label": "Developer Fee"
    },
    "project-location-state-code": {
      "locator": {
        "label": "#B16",
        "type": "cell",
        // "direction": "right",
        "workSheet": "Inputs"
      },
      "valueKey": "project-location-state-code",
      "label": "Project Location (State Code)"
    },
    "month-and-year-pis-cod": {
      "locator": {
        "label": "#B19",
        "type": "cell",
        // "direction": "right",
        "workSheet": "Inputs"
      },
      "valueKey": "month-and-year-pis-cod",
      "label": "Month and Year PIS/COD (mm/dd/yyyy)"
    },
    "lifetime-of-project": {
      "locator": {
        "label": "#B20",
        "type": "cell",
        // "direction": "right",
        "workSheet": "Inputs"
      },
      "valueKey": "lifetime-of-project",
      "label": "Lifetime of Project"
    },
    "market-value-of-project-in-5-years": {
      "locator": {
        "label": "#B21",
        "type": "cell",
        // "direction": "right",
        "workSheet": "Inputs"
      },
      "valueKey": "market-value-of-project-in-5-years",
      "label": "Expected Market Value of Project in 5 years"
    },
  },
  "configName": "pfa-assumptions",
  "configKey": "pfa-assumptions",
  "locatorMap": {}
};

export const ProjectPricingScenarioConfiguration: WhatIfScenarioConfig & any  = {
  "mappingConfig": {
    "location-state": {
      "locator": {
        "label": "#B4",
        "type": "cell",
        // "direction": "right",
        "workSheet": "Pricing"
      },
      "valueKey": "location-state",
      "label": "Location (State)"
    },
    "system-size": {
      "locator": {
        "label": "#B5",
        "type": "cell",
        // "direction": "right",
        "workSheet": "Pricing"
      },
      "valueKey": "system-size",
      "label": "System Size (kW)"
    },
    "cost": {
      "locator": {
        "label": "#B6",
        "type": "cell",
        // "direction": "right",
        "workSheet": "Pricing"
      },
      "valueKey": "cost",
      "label": "Cost ($/kW)"
    },
    "lifetime": {
      "locator": {
        "label": "#B7",
        "type": "cell",
        // "direction": "right",
        "workSheet": "Pricing"
      },
      "valueKey": "lifetime",
      "label": "Lifetime (years)"
    },
    "opex": {
      "locator": {
        "label": "#B8",
        "type": "cell",
        // "direction": "right",
        "workSheet": "Pricing"
      },
      "valueKey": "opex",
      "label": "OpEx ($)"
    },
    "other-revenue": {
      "locator": {
        "label": "#B9",
        "type": "cell",
        // "direction": "right",
        "workSheet": "Pricing"
      },
      "valueKey": "other-revenue",
      "label": "Other Revenue ($)"
    },
    "upfront-rebates": {
      "locator": {
        "label": "#B10",
        "type": "cell",
        // "direction": "right",
        "workSheet": "Pricing"
      },
      "valueKey": "upfront-rebates",
      "label": "Upfront Rebates ($)"
    },
    "debt": {
      "locator": {
        "label": "#B11",
        "type": "cell",
        // "direction": "right",
        "workSheet": "Pricing"
      },
      "valueKey": "debt",
      "label": "Debt ($)"
    },
    "interest-rate": {
      "locator": {
        "label": "#B12",
        "type": "cell",
        // "direction": "right",
        "workSheet": "Pricing",
        "extra": {
          "valueType": "percentx100"
        }
      },
      "valueKey": "interest-rate",
      "label": "Interest Rate (%)"
    },
    "debt-term": {
      "locator": {
        "label": "#B13",
        "type": "cell",
        // "direction": "right",
        "workSheet": "Pricing"
      },
      "valueKey": "debt-term",
      "label": "Debt Term (years)"
    },
    "target-irr": {
      "locator": {
        "label": "#B14",
        "type": "cell",
        // "direction": "right",
        "workSheet": "Pricing",
        "extra": {
          "valueType": "percentx100"
        }
      },
      "valueKey": "target-irr",
      "label": "Target IRR (%)"
    },
  },
  "configName": "project-pricing-assumptions",
  "configKey": "project-pricing-assumptions",
  "locatorMap": {}
};

export const ProjectCreationScenarioConfiguration: WhatIfScenarioConfig & any = {
  "mappingConfig": {
    "project-name": {
      "locator": { "workSheet": "Sales Team Inputs", "type": "parameter", "direction": "right", "label": "Project Name" }, "dataSource": null, "label": "Project Name", "valueKey": "project-name"
    },
    "construction-beginning-date": {
      "locator": { "label": "Construction Beginning Date", "type": "parameter", "direction": "right", "workSheet": "Sales Team Inputs" }, "dataSource": null, "valueKey": "construction-beginning-date", "label": "Construction Start Month (mm/yyyy)"
    },
    // "construction-period-months": {
    //   "locator": { "label": "Construction Period (Months)", "type": "parameter", "direction": "right", "workSheet": "Sales Team Inputs" }, "dataSource": null, "valueKey": "construction-period-months", "label": "Construction Period (In Months)"
    // },
    "model-period-year": {
      "locator": { "label": "Model Period (year)", "type": "parameter", "direction": "right", "workSheet": "Sales Team Inputs" }, "dataSource": null, "valueKey": "model-period-year", "label": "Project Lifetime (In Years)"
    },
    // "address-line-1": {
    //    "locator": { "label": "Address Line-1", "type": "parameter", "direction": "right", "workSheet": "Sales Team Inputs" }, "dataSource": null, "valueKey": "address-line-1", "label": "Address Line-1" 
    // },
    // "county": { "locator": { "label": "County", "type": "parameter", "direction": "right", "workSheet": "Sales Team Inputs" }, "dataSource": null, "valueKey": "county", "label": "County" },
    "state": { "locator": { "label": "State", "type": "parameter", "direction": "right", "workSheet": "Sales Team Inputs" }, "dataSource": null, "valueKey": "state", "label": "State" },
    // "zip-code": { "locator": { "label": "Zip Code", "type": "parameter", "direction": "right", "workSheet": "Sales Team Inputs" }, "dataSource": null, "valueKey": "zip-code", "label": "Zip Code" },
    "pv-system-size-kW-DC": { "locator": { "label": "PV System Size (kW DC)", "type": "parameter", "direction": "right", "workSheet": "Sales Team Inputs" }, "dataSource": null, "valueKey": "pv-system-size-kW-DC", "label": "PV System Size (kW DC)" },
    "pv-system-size-kW-AC": { "locator": { "label": "PV System Size (kW AC)", "type": "parameter", "direction": "right", "workSheet": "Sales Team Inputs" }, "dataSource": null, "valueKey": "pv-system-size-kW-AC", "label": "PV System Size (kW AC)" },
    "residential-avoided-rate-($/kWh)": { "locator": { "label": "Residential Avoided Rate ($/kWh)", "type": "parameter", "direction": "right", "workSheet": "Sales Team Inputs" }, "dataSource": null, "valueKey": "residential-avoided-rate-($/kWh)", "label": "Avoided Rate ($/kWh)" },
    // "Discount": { "locator": { "label": "Discount", "type": "parameter", "direction": "right", "extra": { "valueType": "percentx100" }, "workSheet": "Sales Team Inputs" }, "dataSource": null, "valueKey": "Discount", "label": "Discount (%)" },
    "residential-ppa-rate-$/kWh)": { "locator": { "label": "Residential PPA Rate ($/kWh)", "type": "parameter", "direction": "right", "workSheet": "Sales Team Inputs" }, "dataSource": null, "valueKey": "residential-ppa-rate-$/kWh)", "label": "Desired PPA Rate ($/kWh)" },
    "ppa-rate-escalator-(%)": { "locator": { "workSheet": "Sales Team Inputs", "type": "parameter", "direction": "right", "label": "PPA Rate Escalator", "extra": { "valueType": "percentx100" } }, "dataSource": null, "label": "PPA Rate Escalator (%)", "valueKey": "ppa-rate-escalator-(%)" },
    "system-cost-($/watt dc)": { "locator": { "label": "System Cost ($/watt dc)", "type": "parameter", "direction": "right", "workSheet": "Finance Team Inputs" }, "dataSource": null, "label": "System Cost ($/watt dc)", "valueKey": "system-cost-($/watt dc)" },
    "debt-interest-rate-(%)": { "locator": { "type": "parameter", "direction": "right", "label": "Debt Interest Rate", "workSheet": "Finance Team Inputs", "extra": { "valueType": "percentx100" } }, "dataSource": null, "valueKey": "debt-interest-rate", "label": "Debt Interest Rate (%)" },
    "debt-term": { "locator": { "type": "parameter", "direction": "right", "label": "Debt Term", "workSheet": "Finance Team Inputs" }, "dataSource": null, "valueKey": "debt-term", "label": "Debt Term (Years)" },
    "3p-professional-services-fee": { "locator": { "type": "parameter", "direction": "right", "label": "3P Professional Services Fee", "workSheet": "Finance Team Inputs", "extra": { "valueType": "percentx100" } }, "dataSource": null, "valueKey": "3p-professional-services-fee", "label": "3P Professional Services Fee (%)" },
    "3p-per-unit-set-up-fees-($)": { "locator": { "workSheet": "Finance Team Inputs", "type": "parameter", "label": "3P Per Unit Set Up Fees", "direction": "right" }, "dataSource": null, "label": "3P Per Unit Set Up Fees", "valueKey": "3p-per-unit-set-up-fees-($)" },
    "pv-system-o&m-($/kW)": { "locator": { "label": "PV System O&M ($/kW)", "direction": "right", "workSheet": "Finance Team Inputs", "type": "parameter" }, "dataSource": null, "label": "PV System O&M ($/kW)", "valueKey": "pv-system-o&m-($/kW)" },
    "3p-unit-billing-fees-($/unit/month)": { "locator": { "label": "3P Unit Billing Fees ($/Unit/Month)", "type": "parameter", "direction": "right", "workSheet": "Finance Team Inputs" }, "dataSource": null, "label": "3P Unit Billing Fees ($/Unit/Month)", "valueKey": "3p-unit-billing-fees-($/unit/month)" },
    "3p-unit-billing-fees-inflation-rate-(%)": { "locator": { "label": "3P Unit Billing Fees Inflation Rate", "type": "parameter", "direction": "right", "workSheet": "Finance Team Inputs", "extra": { "valueType": "percentx100" } }, "dataSource": null, "valueKey": "3p-unit-billing-fees-inflation-rate-(%)", "label": "3P Unit Billing Fees Inflation Rate (%)" },
    "ps-asset-management-fee-($/unit/year)": { "locator": { "label": "PS Asset Management Fee ($/Unit/Year)", "type": "parameter", "direction": "right", "workSheet": "Finance Team Inputs" }, "dataSource": null, "label": "PS Asset Management Fee ($/Unit/Year)", "valueKey": "ps-asset-management-fee-($/unit/year)" },
    "ps-asset-management-fee-period-years": { "locator": { "label": "PS Asset Management Fee Period (Years)", "type": "parameter", "direction": "right", "workSheet": "Finance Team Inputs" }, "dataSource": null, "label": "PS Asset Management Fee Period (Years)", "valueKey": "ps-asset-management-fee-period-years" },
    "ps-arrangement-fee-on-itc-(%)": { "locator": { "label": "PS Arrangement Fee on ITC", "type": "parameter", "direction": "right", "workSheet": "Finance Team Inputs", "extra": { "valueType": "percentx100" } }, "dataSource": null, "valueKey": "ps-arrangement-fee-on-itc-(%)", "label": "PS Arrangement Fee on ITC (%)" },
    "ps-arrangement-fee-on-debt-(%)": { "locator": { "label": "PS Arrangement Fee on Debt", "type": "parameter", "direction": "right", "workSheet": "Finance Team Inputs", "extra": { "valueType": "percentx100" } }, "dataSource": null, "valueKey": "ps-arrangement-fee-on-debt-(%)", "label": "PS Arrangement Fee on Debt (%)" },
    "ps-arrangement-fee-on-equity-(%)": { "locator": { "label": "PS Arrangement Fee on Equity", "type": "parameter", "direction": "right", "workSheet": "Finance Team Inputs", "extra": { "valueType": "percentx100" } }, "dataSource": null, "valueKey": "ps-arrangement-fee-on-equity-(%)", "label": "PS Arrangement Fee on Equity (%)" },
    "surface-area-sqft": { "locator": { "label": "Surface Area (sqft)", "type": "parameter", "direction": "right", "workSheet": "Finance Team Inputs" }, "dataSource": null, "valueKey": "surface-area-sqft", "label": "Surface Area (sqft)" },
    "lease-$/sqft": { "locator": { "label": "Lease $/sqft", "type": "parameter", "direction": "right", "workSheet": "Finance Team Inputs" }, "dataSource": null, "valueKey": "lease-$/sqft", "label": "Desired Lease $/sqft ($)" },
    "lease-term-years": { "locator": { "label": "Lease Term (Years)", "type": "parameter", "direction": "right", "workSheet": "Finance Team Inputs" }, "dataSource": null, "valueKey": "lease-term-years", "label": "Lease Term (Years)" },
    "lease-escalator": { "locator": { "label": "Lease Escalator", "type": "parameter", "direction": "right", "workSheet": "Finance Team Inputs", "extra": { "valueType": "percentx100" } }, "dataSource": null, "valueKey": "lease-escalator", "label": "Lease Escalator (%)" }
  }, "configName": "project-creation-assumptions", "configKey": "project-creation-assumptions", "locatorMap": {}
}

export const ProjectCreationPFATScenarioConfiguration: WhatIfScenarioConfig & any = {
  "mappingConfig": {
    "project-name": {
      "locator": { "workSheet": "Sales Team Inputs", "type": "parameter", "direction": "right", "label": "Project Name" }, "dataSource": null, "label": "Project Name", "valueKey": "project-name"
    },
    "construction-beginning-date": {
      "locator": { "label": "Construction Beginning Date", "type": "parameter", "direction": "right", "workSheet": "Sales Team Inputs" }, "dataSource": null, "valueKey": "construction-beginning-date", "label": "Construction Start Month (mm/yyyy)"
    },
    // "construction-period-months": {
    //   "locator": { "label": "Construction Period (Months)", "type": "parameter", "direction": "right", "workSheet": "Sales Team Inputs" }, "dataSource": null, "valueKey": "construction-period-months", "label": "Construction Period (In Months)"
    // },
    "model-period-year": {
      "locator": { "label": "Model Period (year)", "type": "parameter", "direction": "right", "workSheet": "Sales Team Inputs" }, "dataSource": null, "valueKey": "model-period-year", "label": "Project Lifetime (In Years)"
    },
    // "address-line-1": {
    //    "locator": { "label": "Address Line-1", "type": "parameter", "direction": "right", "workSheet": "Sales Team Inputs" }, "dataSource": null, "valueKey": "address-line-1", "label": "Address Line-1" 
    // },
    // "county": { "locator": { "label": "County", "type": "parameter", "direction": "right", "workSheet": "Sales Team Inputs" }, "dataSource": null, "valueKey": "county", "label": "County" },
    "state": { "locator": { "label": "State", "type": "parameter", "direction": "right", "workSheet": "Sales Team Inputs" }, "dataSource": null, "valueKey": "state", "label": "State" },
    // "zip-code": { "locator": { "label": "Zip Code", "type": "parameter", "direction": "right", "workSheet": "Sales Team Inputs" }, "dataSource": null, "valueKey": "zip-code", "label": "Zip Code" },
    "pv-system-size-kW-DC": { "locator": { "label": "PV System Size (kW DC)", "type": "parameter", "direction": "right", "workSheet": "Sales Team Inputs" }, "dataSource": null, "valueKey": "pv-system-size-kW-DC", "label": "PV System Size (kW DC)" },
    "pv-system-size-kW-AC": { "locator": { "label": "PV System Size (kW AC)", "type": "parameter", "direction": "right", "workSheet": "Sales Team Inputs" }, "dataSource": null, "valueKey": "pv-system-size-kW-AC", "label": "PV System Size (kW AC)" },
    "residential-avoided-rate-($/kWh)": { "locator": { "label": "Residential Avoided Rate ($/kWh)", "type": "parameter", "direction": "right", "workSheet": "Sales Team Inputs" }, "dataSource": null, "valueKey": "residential-avoided-rate-($/kWh)", "label": "Avoided Rate ($/kWh)" },
    // "Discount": { "locator": { "label": "Discount", "type": "parameter", "direction": "right", "extra": { "valueType": "percentx100" }, "workSheet": "Sales Team Inputs" }, "dataSource": null, "valueKey": "Discount", "label": "Discount (%)" },
    "residential-ppa-rate-$/kWh)": { "locator": { "label": "Residential PPA Rate ($/kWh)", "type": "parameter", "direction": "right", "workSheet": "Sales Team Inputs" }, "dataSource": null, "valueKey": "residential-ppa-rate-$/kWh)", "label": "Desired PPA Rate ($/kWh)" },
    "ppa-rate-escalator-(%)": { "locator": { "workSheet": "Sales Team Inputs", "type": "parameter", "direction": "right", "label": "PPA Rate Escalator", "extra": { "valueType": "percentx100" } }, "dataSource": null, "label": "PPA Rate Escalator (%)", "valueKey": "ppa-rate-escalator-(%)" },
    "system-cost-($/watt dc)": { "locator": { "label": "System Cost ($/watt dc)", "type": "parameter", "direction": "right", "workSheet": "Finance Team Inputs" }, "dataSource": null, "label": "System Cost ($/watt dc)", "valueKey": "system-cost-($/watt dc)" },
    "debt-interest-rate-(%)": { "locator": { "type": "parameter", "direction": "right", "label": "Debt Interest Rate", "workSheet": "Finance Team Inputs", "extra": { "valueType": "percentx100" } }, "dataSource": null, "valueKey": "debt-interest-rate", "label": "Debt Interest Rate (%)" },
    "debt-term": { "locator": { "type": "parameter", "direction": "right", "label": "Debt Term", "workSheet": "Finance Team Inputs" }, "dataSource": null, "valueKey": "debt-term", "label": "Debt Term (Years)" },
    "surface-area-sqft": { "locator": { "label": "Surface Area (sqft)", "type": "parameter", "direction": "right", "workSheet": "Finance Team Inputs" }, "dataSource": null, "valueKey": "surface-area-sqft", "label": "Surface Area (sqft)" },
    "lease-$/sqft": { "locator": { "label": "Lease $/sqft", "type": "parameter", "direction": "right", "workSheet": "Finance Team Inputs" }, "dataSource": null, "valueKey": "lease-$/sqft", "label": "Desired Lease $/sqft ($)" },
    "lease-term-years": { "locator": { "label": "Lease Term (Years)", "type": "parameter", "direction": "right", "workSheet": "Finance Team Inputs" }, "dataSource": null, "valueKey": "lease-term-years", "label": "Lease Term (Years)" },
    "lease-escalator": { "locator": { "label": "Lease Escalator", "type": "parameter", "direction": "right", "workSheet": "Finance Team Inputs", "extra": { "valueType": "percentx100" } }, "dataSource": null, "valueKey": "lease-escalator", "label": "Lease Escalator (%)" }
  }, "configName": "project-creation-pfat-assumptions", "configKey": "project-creation-pfat-assumptions", "locatorMap": {}
}